"use client";

import { useState } from "react";
import { apiService } from "@/lib/api";
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle, X } from "lucide-react";
import { useAuth } from '@/contexts/AuthContext';

export default function ContactSection() {
  const { isAuthenticated, user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setShowSuccessModal(false);
    try {
      const response: any = await apiService.sendContactMessage(formData);
      if (response?.success) {
        setFormData({ name: "", email: "", phone: "", message: "" });
        setShowSuccessModal(true);
        setError("");
      } else {
        setError(response?.message || "Failed to send message. Please try again.");
      }
    } catch (err: any) {
      setError(err.message || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [showFront, setShowFront] = useState(true);
  return (
    <section
      id="contact"
      className="pt-12 sm:pt-16 lg:pt-20 pb-12 sm:pb-16 lg:pb-20 bg-white dark:bg-dark-900 w-full"
      style={{ contentVisibility: "auto", containIntrinsicSize: "900px" }}
    >
      <div className="container w-full">
        <div className="text-center mb-8 sm:mb-12 px-4">
          <h2 className="text-4xl font-semibold text-primary-700 dark:text-primary-200 mb-4 tracking-tight" style={{fontFamily: 'inherit', letterSpacing: '0.01em'}}>
            Get In Touch
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
            We’re here to help you start your spiritual journey. Reach out for consultations, guidance, or any questions you may have.
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-8 justify-center items-stretch w-full max-w-3xl mx-auto">
          {/* Left: Contact Details */}
          <div className="flex-1 flex flex-col justify-center bg-white dark:bg-dark-900 rounded-xl border border-gray-100 dark:border-gray-800 shadow-md p-6 gap-4 min-w-[240px] max-w-xs mx-auto">
            <h3 className="text-base font-bold text-primary-700 dark:text-primary-200 mb-1">Contact Information</h3>
            <a href="tel:+9779851112728" className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-primary-50 dark:hover:bg-dark-800 transition group">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-500/90 text-white group-hover:bg-primary-600"><Phone className="w-4 h-4" /></span>
              <span className="text-sm text-gray-800 dark:text-gray-100 font-medium tracking-wide">+977 9851112728</span>
            </a>
            <a href="mailto:shantiswasti9@gmail.com" className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-accent-50 dark:hover:bg-dark-800 transition group">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent-500/90 text-white group-hover:bg-accent-600"><Mail className="w-4 h-4" /></span>
              <span className="text-sm text-gray-800 dark:text-gray-100 font-medium tracking-wide">shantiswasti9@gmail.com</span>
            </a>
            <div className="flex items-center gap-2 px-2 py-2 rounded-lg">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-400/90 text-white"><MapPin className="w-4 h-4" /></span>
              <span className="text-sm text-gray-800 dark:text-gray-100 font-medium tracking-wide">Kathmandu, Nepal</span>
            </div>
          </div>
          {/* Right: Message Form (only for logged-in users) */}
          <div className="flex-1 flex flex-col justify-center bg-white dark:bg-dark-900 rounded-xl border border-gray-100 dark:border-gray-800 shadow-md p-6 min-w-[240px] max-w-md mx-auto">
            <h3 className="text-base font-bold text-primary-700 dark:text-primary-200 mb-4">Send us a Message</h3>
            {isAuthenticated ? (
              <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Name</label>
                  <input id="name" name="name" type="text" value={formData.name} onChange={handleChange} required className="input" placeholder="Your name" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Email</label>
                  <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="input" placeholder="your@email.com" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Phone</label>
                  <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="input" placeholder="+977 98XXXXXXXX" pattern="[0-9+\s-]*" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">Message</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="input resize-none" placeholder="Your message..." />
                </div>
                {error && (
                  <div className="mb-2 p-2 bg-red-100 dark:bg-red-900/20 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>{typeof error === "string" ? error : (error as any)?.message || "An error occurred"}</span>
                    </div>
                  </div>
                )}
                <button type="submit" disabled={loading} className="btn btn-primary w-full mt-auto" aria-busy={loading}>
                  {loading ? (
                    <><div className="spinner mr-2"></div>Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" aria-hidden="true" />Send Message</>
                  )}
                </button>
              </form>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-gray-600 dark:text-gray-300 mb-4">Please log in to send us a message.</p>
                <a href="/login" className="btn btn-primary">Login</a>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 relative animate-fadeIn">
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 flex-shrink-0" width={20} height={20} />
            </button>
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Thank You!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Thank you for sending the email. We will get back to you soon.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="btn btn-primary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


